<template>
  <v-autocomplete
    ref="app-search"
    v-model="selected"
    :items="items"
    :loading="loading"
    :search-input.sync="search"
    :clearable="inline"
    :solo="inline"
    :flat="inline"
    :hide-details="inline && 'auto'"
    :rules="rules"
    :disabled="disabled"
    prepend-inner-icon="mdi-apps"
    item-text="name"
    return-object
    placeholder="Search"
    hide-no-data
    data-cy="apps-search"
    @change="change"
  >
    <template v-if="canView()" #append>
      <v-btn
        icon
        :loading="aiLoading"
        :disabled="disabled"
        color="primary"
        data-cy="ai-suggest-btn"
        class="mt-n1"
        @click="fetchAISuggestion"
      >
        <v-icon>mdi-robot</v-icon>
      </v-btn>
    </template>
    <template #item="{ item }">
      <app-dict-search-list-content :app="item" />
    </template>
  </v-autocomplete>
</template>

<script>
import HTTP from '@/api/owl-search';
import { debounce } from 'lodash';
import AppDictSearchListContent from '@/components/mv/AppDictSearchListContent';
import { getConfigMap } from '@/helpers/filters';

function updateSearch(val) {
  if (val) {
    if (!this.selected || val !== this.selected.name) {
      this.fetchData(val);
    }
  }
}

export default {
  components: { AppDictSearchListContent },
  props: {
    value: {
      type: Number,
      default: null,
    },
    inline: {
      type: Boolean,
      default: false,
    },
    rules: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    items: [],
    search: '',
    loading: false,
    selected: null,
    aiLoading: false,
  }),
  watch: {
    search: debounce(updateSearch, 500),
    value(newVal) {
      if (newVal && (!this.selected || this.selected.id !== newVal)) {
        this.findApp(newVal).then(app => {
          this.selected = app;
        });
      } else if (!newVal) {
        this.selected = null;
      }
    },
  },
  async created() {
    if (this.value) {
      this.selected = await this.findApp(this.value);
    }
  },
  methods: {
    findApp(id) {
      this.loading = true;
      return HTTP.get('/apps/find', { params: { id } })
        .then(({ data }) => {
          this.items = data;
          return this.items[0];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    fetchData(search) {
      this.loading = true;
      const params = {
        q: search,
      };
      return HTTP.get('/apps/search', { params })
        .then(({ data }) => {
          if (params.q === this.search) {
            this.items = data;
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    canView() {
      const config = this.getConfigMap();
      return config['mavFeature_owl_ui']?.app_enabled?.v1 || false;
    },
    change(val) {
      const id = val ? val.id : null;
      this.$emit('input', id);
    },
    async fetchAISuggestion() {
      this.aiLoading = true;
      try {
        // Dummy API call - replace with your actual AI API endpoint
        const response = await this.callDummyAIAPI();

        // Find the app by name from the AI response
        if (response.appName) {
          this.search = response.appName;
          await this.fetchData(response.appName);

          if (this.items.length > 0) {
            this.selected = this.items[0];
            this.change(this.selected);
          }
        }
      } catch (error) {
        console.error('AI suggestion failed:', error);
      } finally {
        this.aiLoading = false;
      }
    },
    callDummyAIAPI() {
      // Dummy API call that simulates AI response
      return new Promise(resolve => {
        setTimeout(() => {
          resolve({
            appName: 'Instagram',
            confidence: 0.95,
          });
        }, 1500);
      });
    },
    getConfigMap,
  },
};
</script>
